import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapPinOff } from "lucide-react";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import type { Location } from "@/app/_services/LocationService";

const MANHATTAN_CENTER: [number, number] = [-73.9712, 40.7831];
const MANHATTAN_ZOOM = 12.5;

// Fixed initial camera bounds covering Manhattan + Brooklyn, so the map
// always opens locked to NYC on load regardless of outlier markers
// elsewhere (some locations geocode far outside the city).
const NYC_BOUNDS: maplibregl.LngLatBoundsLike = [
  [-74.005, 40.68], // SW - south Brooklyn
  [-73.92, 40.81], // NE - north Manhattan
];

const MAP_PITCH = 0;

const MAP_TILE_STYLE =
  import.meta.env.VITE_MAP_TILE_URL || "https://tiles.openfreemap.org/styles/positron";

// The "positron" style has ~55 layers total; hiding just symbol/fill-extrusion
// still leaves many line/fill layers (every road class, buildings, landuse,
// landcover, etc.) actively painted every frame - the actual cause of janky
// pan/zoom. Allowlist a handful of layers needed for basic orientation and
// hide everything else, cutting per-frame paint work by ~90%.
// Note: layer IDs are specific to the "positron" style's schema - they don't
// match the "liberty" style's naming (e.g. `highway_minor` vs `road_minor`).
const VISIBLE_LAYER_IDS = new Set([
  "background",
  "water",
  "park",
  "landcover_wood",
  "highway_motorway_casing",
  "highway_motorway_inner",
  "highway_major_casing",
  "highway_major_inner",
  "highway_minor",
  "highway-name-major",
]);

function simplifyBasemap(map: maplibregl.Map) {
  const style = map.getStyle();
  style?.layers?.forEach((layer) => {
    if (!VISIBLE_LAYER_IDS.has(layer.id)) {
      map.setLayoutProperty(layer.id, "visibility", "none");
    }
  });
}

export default function LocationsMap({
  locations,
  selectedLocation,
  onSelectLocation,
}: {
  locations: Location[];
  selectedLocation: Location | null;
  onSelectLocation: (location: Location) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const onSelectLocationRef = useRef(onSelectLocation);
  onSelectLocationRef.current = onSelectLocation;
  const navigate = useNavigate();
  const [mapUnavailable, setMapUnavailable] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: MAP_TILE_STYLE,
        center: MANHATTAN_CENTER,
        zoom: MANHATTAN_ZOOM,
        pitch: MAP_PITCH,
        attributionControl: false,
        // Cap the render resolution on high-DPI (retina) screens - at devicePixelRatio 2-3,
        // every zoom/pan re-tessellates and rasterizes 4-9x the pixels for no visible gain
        // at this map's size, and is the single biggest cause of zoom/pan jank.
        pixelRatio: Math.min(window.devicePixelRatio || 1, 1.5),
      });
    } catch (err) {
      // WebGL can be unavailable/blocked in some browser/sandbox environments
      // (e.g. GPU disabled) even when maplibregl.supported() optimistically
      // returns true - fall back to a message instead of crashing the page.
      console.error("Failed to initialize map", err);
      setMapUnavailable(true);
      return;
    }
    map.addControl(new maplibregl.NavigationControl(), "top-right");
    map.once("load", () => {
      simplifyBasemap(map);
      map.fitBounds(NYC_BOUNDS, { duration: 0, pitch: MAP_PITCH });
    });
    map.on("error", (e) => console.error("Map error", e.error));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    function syncMarkers() {
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current.clear();

      const withCoords = locations.filter(
        (l) => l.latitude != null && l.longitude != null
      );

      withCoords.forEach((location) => {
        const marker = new maplibregl.Marker({ color: "#000000", scale: 0.65 })
          .setLngLat([location.longitude!, location.latitude!])
          .addTo(map!);
        marker.getElement().addEventListener("click", (e) => {
          e.stopPropagation();
          onSelectLocationRef.current(location);
        });
        markersRef.current.set(location.id, marker);
      });
    }

    if (map.loaded()) {
      syncMarkers();
    } else {
      map.once("load", syncMarkers);
    }
  }, [locations]);

  useEffect(() => {
    const map = mapRef.current;
    if (
      !map ||
      !selectedLocation ||
      selectedLocation.latitude == null ||
      selectedLocation.longitude == null
    ) {
      return;
    }

    map.easeTo({
      center: [selectedLocation.longitude, selectedLocation.latitude],
      zoom: 14,
      pitch: MAP_PITCH,
      duration: 400,
    });

    popupRef.current?.remove();
    const address = [
      selectedLocation.address_line_1,
      selectedLocation.city,
      selectedLocation.state,
      selectedLocation.zip,
    ]
      .filter(Boolean)
      .join(", ");

    const showCount = selectedLocation.show_count ?? 0;
    const revenueFormat = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    const lastShowDate = selectedLocation.last_show_date
      ? new Date(selectedLocation.last_show_date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;

    const popupNode = document.createElement("div");
    popupNode.style.fontSize = "13px";
    const title = document.createElement("strong");
    title.textContent = selectedLocation.name;
    title.style.cursor = "pointer";
    title.style.textDecoration = "none";
    title.addEventListener("mouseenter", () => {
      title.style.textDecoration = "underline";
    });
    title.addEventListener("mouseleave", () => {
      title.style.textDecoration = "none";
    });
    title.addEventListener("click", () => {
      navigate(`/admin/locations/${selectedLocation.id}`);
    });
    const subtitle = document.createElement("div");
    subtitle.textContent = address;
    const stats = document.createElement("div");
    stats.style.marginTop = "6px";
    stats.style.paddingTop = "6px";
    stats.style.borderTop = "1px solid hsl(var(--border))";
    stats.style.display = "flex";
    stats.style.alignItems = "center";
    stats.style.gap = "8px";

    if (selectedLocation.last_show_img_url) {
      const thumb = document.createElement("img");
      thumb.src = selectedLocation.last_show_img_url;
      thumb.alt = selectedLocation.last_show_name ?? "";
      thumb.style.width = "40px";
      thumb.style.height = "40px";
      thumb.style.objectFit = "cover";
      thumb.style.borderRadius = "4px";
      thumb.style.flexShrink = "0";
      stats.appendChild(thumb);
    }

    const statsText = document.createElement("div");
    statsText.style.display = "flex";
    statsText.style.flexDirection = "column";
    statsText.style.gap = "2px";

    const showCountRow = document.createElement("div");
    showCountRow.textContent = `${showCount} show${showCount === 1 ? "" : "s"}`;
    statsText.appendChild(showCountRow);

    const revenueRow = document.createElement("div");
    revenueRow.textContent = `Revenue: ${revenueFormat.format(selectedLocation.total_revenue ?? 0)}`;
    statsText.appendChild(revenueRow);

    if (lastShowDate) {
      if (selectedLocation.last_show_id) {
        const lastShowButton = document.createElement("button");
        lastShowButton.type = "button";
        lastShowButton.textContent = `Last show: ${lastShowDate}`;
        lastShowButton.style.color = "hsl(var(--primary))";
        lastShowButton.style.textDecoration = "underline";
        lastShowButton.style.cursor = "pointer";
        lastShowButton.style.textAlign = "left";
        lastShowButton.style.padding = "0";
        lastShowButton.style.background = "none";
        lastShowButton.style.border = "none";
        lastShowButton.style.font = "inherit";
        lastShowButton.addEventListener("click", () => {
          navigate(`/admin/shows/${selectedLocation.last_show_id}/details`);
        });
        statsText.appendChild(lastShowButton);
      } else {
        const lastShowRow = document.createElement("div");
        lastShowRow.textContent = `Last show: ${lastShowDate}`;
        statsText.appendChild(lastShowRow);
      }
    }

    stats.appendChild(statsText);

    popupNode.appendChild(title);
    popupNode.appendChild(subtitle);
    popupNode.appendChild(stats);

    popupRef.current = new maplibregl.Popup({ closeOnClick: false })
      .setLngLat([selectedLocation.longitude, selectedLocation.latitude])
      .setDOMContent(popupNode)
      .addTo(map);
  }, [selectedLocation]);

  return (
    <>
      <style>
        {`
          .maplibregl-marker svg ellipse { display: none !important; }
          .maplibregl-popup-content {
            border: 1px solid hsl(var(--border));
            border-radius: 6px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          }
        `}
      </style>
      {mapUnavailable ? (
        <div className="flex h-full max-h-[calc(100vh-180px)] min-h-[calc(100vh-180px)] w-full flex-col overflow-hidden rounded-md border border-border">
          <CuoEmpty icon={MapPinOff} text="Map unavailable in this browser" />
        </div>
      ) : (
        <div
          ref={containerRef}
          className="h-full max-h-[calc(100vh-180px)] min-h-[calc(100vh-180px)] w-full overflow-hidden rounded-md border border-border"
        />
      )}
    </>
  );
}
